import { parseYearMonth } from "@repo/shared";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { inputChassis } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDialogGuard } from "@/hooks/use-dialog-guard";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./month-year-picker.i18n";

type MonthYearPickerProps = {
  value?: string | null; // YYYY-MM format
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  id?: string; // For debugging
};

export function MonthYearPicker({
  value,
  onChange: onChangeProp,
  placeholder,
  disabled = false,
  className,
}: MonthYearPickerProps) {
  const t = useT(T);
  const { markDirty } = useDialogGuard();

  // Month and year cells are bare buttons, so the change is only observable
  // once it reports itself.
  const onChange = useCallback(
    (next: string | null) => {
      markDirty();
      onChangeProp(next);
    },
    [markDirty, onChangeProp],
  );

  const months = t.months as string[];
  const fullMonths = t.fullMonths as string[];
  const [open, setOpen] = useState(false);
  const effectivePlaceholder = placeholder || (t.selectMonth as string);

  // Parse the current value as a string — never through `Date` (QL-692: a
  // UTC-parsed "YYYY-MM-01" reads as the previous month west of UTC).
  const current = value ? parseYearMonth(value) : null;
  const currentYear = current?.year;
  const currentMonth = current?.monthIndex;

  // Year being viewed in the picker (defaults to current value's year or current year)
  const [viewYear, setViewYear] = useState(() => {
    if (currentYear) return currentYear;
    return new Date().getFullYear();
  });

  // Update viewYear when value changes
  useEffect(() => {
    if (currentYear) {
      setViewYear(currentYear);
    }
  }, [currentYear]);

  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();

  const handleSelectMonth = useCallback(
    (month: number) => {
      const yearStr = viewYear.toString();
      const monthStr = (month + 1).toString().padStart(2, "0");
      onChange(`${yearStr}-${monthStr}`);
      setOpen(false);
    },
    [viewYear, onChange],
  );

  const handleSelectToday = useCallback(() => {
    const yearStr = todayYear.toString();
    const monthStr = (todayMonth + 1).toString().padStart(2, "0");
    onChange(`${yearStr}-${monthStr}`);
    setOpen(false);
  }, [todayYear, todayMonth, onChange]);

  const handleClear = useCallback(() => {
    onChange(null);
    setOpen(false);
  }, [onChange]);

  // Can't go to previous years (all months would be disabled)
  const canGoPrevYear = viewYear > todayYear;

  const handlePrevYear = useCallback(() => {
    if (canGoPrevYear) {
      setViewYear((y) => y - 1);
    }
  }, [canGoPrevYear]);

  const handleNextYear = useCallback(() => {
    setViewYear((y) => y + 1);
  }, []);

  // Check if a month is in the past (before current month)
  const isMonthDisabled = (monthIndex: number) => {
    if (viewYear < todayYear) return true;
    if (viewYear === todayYear && monthIndex < todayMonth) return true;
    return false;
  };

  // Format display value
  const displayValue = current ? `${fullMonths[current.monthIndex]} ${current.year}` : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            inputChassis,
            "items-center justify-between gap-2 text-left text-sm",
            !displayValue && "text-muted-foreground",
            className,
          )}
        >
          <span className="line-clamp-1 min-w-0">{displayValue || effectivePlaceholder}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[280px] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-3">
          {/* Year navigation */}
          <div className="flex items-center justify-between mb-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              tooltip={t.prevYear}
              className="h-7 w-7"
              onClick={handlePrevYear}
              disabled={!canGoPrevYear}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-semibold text-sm">{viewYear}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              tooltip={t.nextYear}
              className="h-7 w-7"
              onClick={handleNextYear}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-3 gap-1">
            {months.map((month, index) => {
              const isSelected = currentYear === viewYear && currentMonth === index;
              const isToday = todayYear === viewYear && todayMonth === index;
              const isDisabled = isMonthDisabled(index);

              return (
                <Button
                  type="button"
                  key={month}
                  variant={isSelected ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-[26px] text-xs font-normal",
                    isToday && !isSelected && "border border-primary",
                  )}
                  onClick={() => handleSelectMonth(index)}
                  disabled={isDisabled}
                >
                  {month}
                </Button>
              );
            })}
          </div>

          {/* Quick actions */}
          <div className="flex gap-2 mt-3 pt-3 border-t">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={handleSelectToday}
            >
              {t.thisMonth as string}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="flex-1"
              onClick={handleClear}
            >
              {t.clear as string}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
